// src/components/EmployeeView/StatusView.js
import { useEffect, useState } from "react";
import { FiMoreVertical } from "react-icons/fi";
import {
  Spinner,
  Modal,
  Button,
} from "react-bootstrap";
import axios from "axios";
import socket from "../../socket"; 
import { useAuth } from "../../context/authContext";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const DEFAULT_PROFILE_IMAGE =
  "https://cdn.jsdelivr.net/gh/twbs/icons@1.10.0/icons/person-circle.svg";

export default function StatusView() {

  const [mainTab, setMainTab] = useState("offices");

  const { isAdmin } = useAuth();

  const [officeTabs, setOfficeTabs] = useState([]);

  const [activeOfficeTab, setActiveOfficeTab] = useState(null);
  const [officeEmployees, setOfficeEmployees] = useState([]);
  const [officeLoading, setOfficeLoading] = useState(false);
  const [officeCounts, setOfficeCounts] = useState({});

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [employeeToDeactivate, setEmployeeToDeactivate] = useState(null);

  const [attendanceTab, setAttendanceTab] = useState("site");
  const [siteEmployees, setSiteEmployees] = useState([]);
  const [siteLoading, setSiteLoading] = useState(false);

  const [wfhEmployees, setWfhEmployees] = useState([]);
  const [wfhLoading, setWfhLoading] = useState(false);
  const [activeMenuEmployee, setActiveMenuEmployee] = useState(null);

  const [selectDeactivateEmployee, setDeactivateEmployee] = useState(null);

  const [dislayMenuIcon, setDisplayMenuIcon] = useState(true);



  const toggleMenu = (e) => {
    setActiveMenuEmployee((prev) => (prev === e.name ? null : e.name));
    setDeactivateEmployee(e);
  };

  const deactivateAccount = async () => {
    try {
      const employee = employeeToDeactivate || selectDeactivateEmployee;
      if (!employee) return;

      const deactiveAccountResponse = await axios.post(
        `${API_URL}/api/deactivate-account`,
        { name: employee.name }
      );

      if (deactiveAccountResponse.status === 200) {
        // Optimistically update all relevant state
        const updateEmployeeLists = (prev) =>
          prev.filter((e) => e.name !== employee.name);

        setOfficeEmployees(updateEmployeeLists);
        setSiteEmployees(updateEmployeeLists);
        setWfhEmployees(updateEmployeeLists);

        // Update office counts if needed
        if (activeOfficeTab) {
          setOfficeCounts((prev) => {
            const currentCount = prev[activeOfficeTab] || {
              present: 0,
              total: 0,
            };
            return {
              ...prev,
              [activeOfficeTab]: {
                present:
                  employee.status.toLowerCase() === "online"
                    ? currentCount.present - 1
                    : currentCount.present,
                total: currentCount.total - 1,
              },
            };
          });
        }

        // Force refresh all relevant data
        if (activeOfficeTab) {
          refreshOffice(activeOfficeTab);
        }
        if (attendanceTab === "site") {
          refreshSite();
        }
        if (attendanceTab === "wfh") {
          refreshWfh();
        }
      } else {
        toast.error("Failed to deactivate account");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error while deactivating account");
    } finally {
      setDeactivateEmployee(null);
      setShowDeactivateModal(false);
      setActiveMenuEmployee(null);
    }
  };

  // ─── SOCKET.IO REAL-TIME ─────────────────────────
  useEffect(() => {
    socket.connect();
    return () => void socket.disconnect();
  }, []);

  useEffect(() => {
    socket.on("attendanceChanged", () => {
      if (activeOfficeTab) {
        refreshOffice(activeOfficeTab);
      }

      if (attendanceTab === "site") {
        refreshSite();
      }
      if (attendanceTab === "wfh") {
        refreshWfh();
      }
    });
    return () => void socket.off("attendanceChanged");
  }, [activeOfficeTab, attendanceTab]);

  // ─── FETCH HELPERS ───────────────────────────────
  const refreshOffice = (office) => {
    // setDisplayMenuIcon(true);
    setOfficeLoading(true);
    axios
      .get(`${API_URL}/api/office-status`, { params: { office } })
      .then((res) => {
        const allEmps = res.data || [];
        allEmps.sort((a, b) => a.name.localeCompare(b.name));
        setOfficeEmployees(allEmps);
        const present = allEmps.filter(
          (e) => e.status.toLowerCase() === "online"
        ).length;
        setOfficeCounts((prev) => ({
          ...prev,
          [office]: { present, total: allEmps.length },
        }));
      })
      .catch(() => setOfficeEmployees([]))
      .finally(() => setOfficeLoading(false));
  };

  const refreshSite = () => {
    // setDisplayMenuIcon(false);
    setSiteLoading(true);
    axios
      .get(`${API_URL}/api/site-status`)
      .then((res) => {
        const allEmps = res.data || [];
        allEmps.sort((a, b) => a.name.localeCompare(b.name));
        setSiteEmployees(allEmps);
      })
      .catch(() => setSiteEmployees([]))
      .finally(() => setSiteLoading(false));
  };

  const refreshWfh = () => {
    // setDisplayMenuIcon(false);
    setWfhLoading(true);
    axios
      .get(`${API_URL}/api/wfh-status`)
      .then((res) => {
        const allEmps = res.data || [];
        allEmps.sort((a, b) => a.name.localeCompare(b.name));
        setWfhEmployees(allEmps);
      })
      .catch(() => setWfhEmployees([]))
      .finally(() => setWfhLoading(false));
  };

  // ─── INITIAL OFFICE LIST + COUNTS ────────────────
  useEffect(() => {
    axios
      .get(`${API_URL}/api/nickoffices`)
      .then((res) => {
        const offices = res.data || [];
        setOfficeTabs(offices);
        if (offices.length) setActiveOfficeTab(offices[0]);

        offices.forEach((office) => {
          axios
            .get(`${API_URL}/api/office-status`, { params: { office } })
            .then((r) => {
              const all = r.data || [];
              const present = all.filter(
                (e) => e.status.toLowerCase() === "online"
              ).length;
              setOfficeCounts((p) => ({
                ...p,
                [office]: { present, total: all.length },
              }));
            })
            .catch(() =>
              setOfficeCounts((p) => ({
                ...p,
                [office]: { present: 0, total: 0 },
              }))
            );
        });
      })
      .catch(() => setOfficeTabs([]));
  }, []);

  // ─── ACTIVE OFFICE CHANGE ────────────────────────
  useEffect(() => {
    if (!activeOfficeTab) return;
    refreshOffice(activeOfficeTab);
  }, [activeOfficeTab]);

  // ─── SITE-VISIT TAB ──────────────────────────────
  useEffect(() => {
    if (attendanceTab === "site") {
      refreshSite();
    }
  }, [attendanceTab]);

  // ─── WFH TAB ─────────────────────────────────────
  useEffect(() => {
    if (attendanceTab === "wfh") {
      refreshWfh();
    }
  }, [attendanceTab]);

  // ─── RENDER GRID ────────────────────────────────
  const getProfileSrc = (emp) =>
    emp.photo_url?.startsWith("http")
      ? emp.photo_url
      : emp.image_filename
      ? `${API_URL}/uploads/${emp.image_filename}`
      : DEFAULT_PROFILE_IMAGE;

  const getStatusDot = (status, colorScheme) => {
    const s = status?.toLowerCase();
    if (s === "online") return colorScheme === "site" ? "#eab308" : "#22c55e";
    if (s === "offline") return "#ef4444";
    return "#f97316"; // absent / unknown → orange
  };

  const getStatusBg = (status, colorScheme) => {
    const s = status?.toLowerCase();
    if (s === "online") {
      if (colorScheme === "site") return { bg: "#fef9c3", border: "#fde047" }; // yellow
      return { bg: "#dcfce7", border: "#86efac" }; // green
    }
    if (s === "offline") return { bg: "#fee2e2", border: "#fca5a5" };
    return { bg: "#ffedd5", border: "#fdba74" }; // absent → orange
  };

  const renderEmployeeGrid = (emps, loading, emptyMsg, colorScheme = "default", officeNames = []) => {
    if (loading)
      return (
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" />
        </div>
      );
    if (!emps.length) return <div className="text-center py-5 text-muted">{emptyMsg}</div>;

    return (
      <div
        style={{
          maxHeight: "75vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
          gap: "16px",
          padding: "4px 4px 12px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {emps.map((e) => {
          const isSiteVisit =
            officeNames.length > 0 &&
            e.location &&
            e.status?.toLowerCase() !== "absent" &&
            e.location.toLowerCase() !== "wfh" &&
            !officeNames.map((o) => o.toLowerCase()).includes(e.location.toLowerCase());
          const effectiveScheme = isSiteVisit ? "site" : colorScheme;
          const { bg, border } = getStatusBg(e.status, effectiveScheme);
          return (
          <div
            key={e.name}
            style={{
              background: bg,
              borderRadius: "14px",
              border: `1px solid ${border}`,
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              padding: "20px 12px 16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              position: "relative",
            }}
          >
            {/* Three-dot menu (admin only) */}
            {isAdmin && dislayMenuIcon && (
              <div
                style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer" }}
                onClick={() => toggleMenu(e)}
              >
                <FiMoreVertical size={18} color="#94a3b8" />
              </div>
            )}

            {/* Dropdown menu */}
            {activeMenuEmployee === e.name && (
              <div
                style={{
                  position: "absolute",
                  top: "32px",
                  right: "10px",
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  zIndex: 100,
                  minWidth: "150px",
                }}
              >
                <div
                  onClick={() => {
                    setEmployeeToDeactivate(e);
                    setShowDeactivateModal(true);
                    setActiveMenuEmployee(null);
                  }}
                  style={{
                    padding: "8px 14px",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    color: "#ef4444",
                    whiteSpace: "nowrap",
                  }}
                >
                  Deactivate Account
                </div>
              </div>
            )}

            {/* Profile photo */}
            <img
              src={getProfileSrc(e)}
              alt={e.name}
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #e8eaed",
                marginBottom: "12px",
              }}
              onError={(ev) => {
                ev.target.onerror = null;
                ev.target.src = DEFAULT_PROFILE_IMAGE;
              }}
            />

            {/* Name */}
            <div style={{ fontWeight: "700", fontSize: "0.875rem", color: "#0f172a", marginBottom: "2px", lineHeight: 1.3 }}>
              {e.name}
            </div>

            {/* Location */}
            <div style={{ fontSize: "0.78rem", color: "#0f172a", marginBottom: "10px", fontWeight: "500" }}>
              {e.location}
            </div>

            {/* Status pill */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "999px",
                border: `1px solid ${border}`,
                background: "#ffffff",
                fontSize: "0.8rem",
                fontWeight: "500",
                color: "#475569",
              }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: getStatusDot(e.status, effectiveScheme),
                  flexShrink: 0,
                }}
              />
            {e.status?.charAt(0).toUpperCase() + e.status?.slice(1)}
            </div>
          </div>
          );
        })}

        {/* Deactivate modal — rendered once outside the loop */}
        <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
          <Modal.Header>
            <Modal.Title>Confirm Deactivation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to deactivate the account for <b>{employeeToDeactivate?.name}</b>?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeactivateModal(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={() => {
                setDeactivateEmployee(employeeToDeactivate);
                deactivateAccount();
                setShowDeactivateModal(false);
                if (activeOfficeTab) refreshOffice(activeOfficeTab);
                setActiveMenuEmployee(null);
              }}
            >
              Deactivate Account
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  // ─── FINAL RENDER ────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", padding: "0 4px" }}>
      {/* ── Main tabs: Offices / Site Visit / WFH ── */}
      <div style={{ borderBottom: "2px solid #e8eaed", marginBottom: "0", background: "#fff" }}>
        <div style={{ display: "flex", gap: "0" }}>
          {[
            { key: "offices", label: "🏢 Offices", onClick: () => setDisplayMenuIcon(true) },
            { key: "attendance", label: "📍 Site Visit / 🏠 WFH", onClick: () => setDisplayMenuIcon(false) },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setMainTab(tab.key); tab.onClick(); }}
              style={{
                background: "none",
                border: "none",
                padding: "10px 20px",
                fontSize: "0.875rem",
                fontWeight: mainTab === tab.key ? "700" : "500",
                color: mainTab === tab.key ? "#4f46e5" : "#64748b",
                borderBottom: mainTab === tab.key ? "3px solid #4f46e5" : "3px solid transparent",
                cursor: "pointer",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: "#fff", padding: "12px 8px 0" }}>
        {mainTab === "offices" && (
          <>
            {/* Office sub-tabs */}
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginBottom: "12px", borderBottom: "1px solid #f1f5f9", paddingBottom: "10px" }}>
              {officeTabs.map((office) => {
                const { present = 0, total = 0 } = officeCounts[office] || {};
                const isActive = activeOfficeTab === office;
                return (
                  <button
                    key={office}
                    onClick={() => setActiveOfficeTab(office)}
                    style={{
                      background: "none",
                      border: "none",
                      padding: "4px 14px",
                      fontSize: "0.82rem",
                      fontWeight: isActive ? "700" : "500",
                      color: isActive ? "#4f46e5" : "#64748b",
                      borderBottom: isActive ? "2px solid #4f46e5" : "2px solid transparent",
                      cursor: "pointer",
                    }}
                  >
                    {office} ({present}/{total})
                  </button>
                );
              })}
            </div>
            {renderEmployeeGrid(officeEmployees, officeLoading, `No employees in "${activeOfficeTab}".`, "default", officeTabs)}
          </>
        )}

        {mainTab === "attendance" && (
          <>
            {/* Site / WFH sub-tabs */}
            <div style={{ display: "flex", gap: "4px", marginBottom: "12px", borderBottom: "1px solid #f1f5f9", paddingBottom: "10px" }}>
              {[
                { key: "site", label: "🛠 Site Visit" },
                { key: "wfh", label: "💻 WFH" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setAttendanceTab(tab.key)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "4px 14px",
                    fontSize: "0.82rem",
                    fontWeight: attendanceTab === tab.key ? "700" : "500",
                    color: attendanceTab === tab.key ? "#4f46e5" : "#64748b",
                    borderBottom: attendanceTab === tab.key ? "2px solid #4f46e5" : "2px solid transparent",
                    cursor: "pointer",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {attendanceTab === "site"
              ? renderEmployeeGrid(siteEmployees, siteLoading, "No site-visit employees.", "site")
              : renderEmployeeGrid(wfhEmployees, wfhLoading, "No WFH employees.")}
          </>
        )}
      </div>
    </div>
  );
}
